/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import dayjs from "dayjs";
import {DatePicker, Form, FormInstance, InputNumber, InputNumberProps, Radio} from "antd";
import styles from './AddForm.module.scss';
import GroupAPI from "../../../../../../../api/groupAPI";
import { ModalDataIF } from "../../../../../BaechaList2.interface";
import {BIG_CAR_TYPES, getIsCanCallPass, getPriceByType, SMALL_CAR_TYPES} from "../../../../../BaechaList2Util";

const getLabelComponent = (type: string, price: number) => (
	<div style={{ textAlign: 'center', height: 80 }}>
		<div>{type}</div>
		<div>{price / 10000}만 원</div>
	</div>
);

interface AddFormProps {
	form: FormInstance<any>;
	modalData: ModalDataIF;
}

const AddForm = ({ form, modalData }: AddFormProps) => {
	const mhType = Form.useWatch('mhType', form);

	const [priceData, setPriceData] = useState<any[]>([]);
	const [isEnableOldPrice, setIsEnableOldPrice] = useState(false);
	const [isEnableMidPrice, setIsEnableMidPrice] = useState(false);

	const baechaCarType = modalData.data?.carType;

	const isSmallCar = !!baechaCarType && SMALL_CAR_TYPES.has(baechaCarType);
	const isBigCar   = !!baechaCarType && BIG_CAR_TYPES.has(baechaCarType);

	const mhTypeOptions = useMemo(() => {
		if (!baechaCarType) return [];

		const base = [
			{ label: '1시간', value: '1h' },
			{ label: '2시간', value: '2h' },
			{ label: '반나절', value: '4h' },
			{ label: '하루', value: '1d' },
		];

		if (['54m','58m','75m','28m','40m'].includes(baechaCarType)) {
			return base.slice(2);
		}

		if (['60m','70m'].includes(baechaCarType)) {
			return [base[3]];
		}

		return base;
	}, [baechaCarType]);

	const priceOptions = useMemo(() => {
		if (!mhType) return [];

		const target = priceData.find((v) => v.mhType === mhType);
		if (!target) return [];

		const options = [];

		if (isEnableOldPrice) {
			options.push({
				label: getLabelComponent('구단가', target.oldPrice),
				value: target.oldPrice,
			});
		}

		if (isEnableMidPrice) {
			options.push({
				label: getLabelComponent('중단가', target.midPrice),
				value: target.midPrice,
			});
		}

		options.push({
			label: getLabelComponent('신단가', target.newPrice),
			value: target.newPrice,
		});

		return options;
	}, [mhType, priceData, isEnableOldPrice, isEnableMidPrice]);

	const formatter: InputNumberProps<number>['formatter'] = (value) => {
		const [start, end] = `${value}`.split('.');
		const v = start.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
		return end ? `${v}.${end}` : v;
	};

	useEffect(() => {
		if (!mhType) return;

		const target = priceData.find(v => v.mhType === mhType);
		if (!target) return;

		const price = getPriceByType(target, modalData.data?.priceType);
		form.setFieldValue('price', isBigCar ? 0 : price);
	}, [mhType, priceData, modalData.data?.priceType, isBigCar]);
	
	useEffect(() => {
		const { baljuingGroupId, carType } = modalData.data || {};
		if (!baljuingGroupId || !carType) return;

		GroupAPI.getGroupConfigs(baljuingGroupId, ['price'])
			.then((res) => {
				setIsEnableOldPrice(res.config.enable_old_price);
				setIsEnableMidPrice(res.config.enable_mid_price);

				const newPriceData = res.price.filter((item: any) => {
					return getIsCanCallPass(item.carType, carType);
				});
				setPriceData(newPriceData);

				form.setFieldsValue({
					workDate: dayjs(),
					mhType: '1d',
					overtimePrice: 0,
				});
			})
			.catch(console.error);
	}, [modalData.data]);

	return (
		<Form
			labelCol={{ xs: { span: 24 }, sm: { span: 4 } }}
			wrapperCol={{ xs: { span: 24 }, sm: { span: 18 } }}
			form={form}
			size="large"
			style={{ width: '100%', marginTop: 20 }}
		>
			<Form.Item label="작업 날짜" name="workDate">
				<DatePicker allowClear={false} />
			</Form.Item>

			<Form.Item label="작업 시간" name="mhType">
				<Radio.Group
					block
					options={mhTypeOptions}
					optionType="button"
					buttonStyle="solid"
				/>
			</Form.Item>

			<Form.Item label="단가" name="price">
				{isSmallCar && (
					<Radio.Group
						className={styles.priceRadioGroup}
						block
						options={priceOptions}
						optionType="button"
						buttonStyle="solid"
					/>
				)}

				{isBigCar && (
					<InputNumber
						step={5}
						suffix="만 원"
						style={{ width: '100%' }}
						formatter={formatter}
					/>
				)}
			</Form.Item>

			<Form.Item label="야간 연장" name="overtimePrice">
				<InputNumber suffix="만 원" style={{ width: '100%' }} />
			</Form.Item>
		</Form>
	);
};

export default AddForm;
